import {
    pointsToPaths,
    drawStraightArrow2,
    drawHammer2,
    drawBezierHammer,
} from "./whiteboard_util";

const SIZE_MAP = [2, 4, 6];
const DRAGGER_STROKE_WIDTH = 10;
const HITBOX_STROKE_WIDTH = 18;

export function CNLineFactory(data) {
    switch (data.t) {
        case "multiline":
            return new Multiline(data);
        case "multilineArrow":
            return new MultilineArrow(data);
        case "multilineHammer":
            return new MultilineHammer(data);
        case "multilineDashedArrow":
            return new MultilineDashedArrow(data);
        case "multilineDotted":
            return new MultilineDotted(data);
        case "penSquigglyTab":
        case "penSquigglyArrow":
            return new PenSquigglyArrow(data);
        case "penSquigglyFirstTab":
        case "penSquigglyFirstArrow":
            return new PenSquigglyFirstArrow(data);
        case "bezierLine":
        case "bezierLineDashed":
        case "bezierLineTab":
        case "bezierLineTabDashed":
            return new BezierLine(data);
    }
}

class CNLine {
    constructor(data) {
        this.name = data.t;
        this.color = data.c;
        this.size = data.th;
        this.points = data.points;
        this.data = data;
        this.removerImage = data.removerImage;
        this.id = this.name + "" + data.drawId;
        this.drawId = data.drawId;

        this.remover = this.buildRemover();
        this.dragger = this.buildDragger();
        this.hitbox = this.buildHitbox();
    }

    pasteData(inc) {
        return this.data.d.map((p) => ({
            x: p.x + 30 * inc,
            y: p.y + 30 * inc,
        }));
    }

    setDragStart(callback) {
        const _this = this;
        this.element.on("dragstart", (e) => {
            callback(e);
            _this.dragger.strokeWidth(DRAGGER_STROKE_WIDTH);
        });
    }

    setDragEnd(callback) {
        this.element.on("dragend", (e) => callback(e));
    }

    setDragClick(callback) {
        const _this = this;
        function click(e) {
            callback(e);
            _this.dragger.strokeWidth(DRAGGER_STROKE_WIDTH);
        }
        this.element.on("click", (e) => click(e));
        this.element.on("tap", (e) => click(e));
    }

    setClickAction(callback) {
        this.element.on("click", () => callback(this.color));
        this.element.on("tap", () => callback(this.color));
    }

    setColor(color) {
        this.color = color;
        this.currentLine.stroke(color);
    }

    buildRemover() {
        const { maxX, maxY } = this.getBoundingBox();
        const remover = new Konva.Image({
            image: this.removerImage,
            width: 20,
            height: 20,
            name: "remover",
            x: maxX + 10,
            y: maxY + 10,
            visible: false,
        });
        remover.on("mouseover", () => {
            document.body.style.cursor = "pointer";
        });
        remover.on("mouseout", () => {
            document.body.style.cursor = "default";
        });

        return remover;
    }

    getBoundingBox() {
        const xs = this.data.d.map((p) => p.x);
        const ys = this.data.d.map((p) => p.y);

        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        const width = maxX - minX;
        const height = maxY - minY;

        return {
            absX: minX,
            absY: minY,
            minX: 0,
            maxX: maxX - minX,
            minY: 0,
            maxY: maxY - minY,
            width: width,
            height: height,
        };
    }

    toRelativePoints() {
        const { absX, absY } = this.getBoundingBox();
        const simplifiedPath = this.data.d.map((p) => ({
            x: p.x - absX,
            y: p.y - absY,
        }));
        return simplifiedPath;
    }

    buildHitbox() {
        return this.dragger.clone({
            name: "hitbox",
            stroke: "rgba(14, 140, 233, 0.01)",
            strokeWidth: HITBOX_STROKE_WIDTH,
            dash: [],
            visible: true,
        });
    }

    buildDragger() {
        const isBezier = this.name.startsWith("bezierLine");
        const dragger = isBezier
            ? new Konva.Shape({
                  sceneFunc: (ctx, shape) => {
                      ctx.beginPath();
                      ctx.moveTo(this.start.x(), this.start.y());
                      ctx.bezierCurveTo(
                          this.control1.x(),
                          this.control1.y(),
                          this.control2.x(),
                          this.control2.y(),
                          this.end.x(),
                          this.end.y()
                      );
                      ctx.strokeShape(shape);
                  },
                  stroke: "rgba(14, 140, 233, 0.5)",
                  strokeWidth: SIZE_MAP[this.size] + 6,
                  dash: [6, 4],
                  lineCap: "round",
                  lineJoin: "round",
                  name: "dragger",
                  visible: false,
              })
            : new Konva.Line({
                  points: pointsToPaths(this.toRelativePoints()),
                  stroke: "rgba(14, 140, 233, 0.5)",
                  strokeWidth: SIZE_MAP[this.size] + 6,
                  dash: [6, 4],
                  lineCap: "round",
                  lineJoin: "round",
                  name: "dragger",
                  visible: false,
              });

        return dragger;
    }

    setRemoverAction(callback) {
        this.removeAction = callback;
        this.remover.on("click", () => {
            callback();
        });
        this.remover.on("tap", () => {
            callback();
        });
    }
}

// Non-bezier lines share local vertex coordinates so editing never moves the group.
class EditableMultiline extends CNLine {
    constructor(data) {
        super(data);
        const { absX, absY } = this.getBoundingBox();
        this.data.pointOrigin = { x: absX, y: absY };
        this.pathPoints = this.toRelativePoints();
        this.anchors = [];
        this.element = this.buildElement();
        this.rebuildAnchors();
        this.element.on("dblclick dbltap", (event) => {
            if (!this.canEditPoints() || event.target === this.remover) return;
            const pointer = this.element.getRelativePointerPosition();
            if (!pointer) return;

            let nearest = null;
            for (let index = 0; index < this.pathPoints.length - 1; index++) {
                const start = this.pathPoints[index];
                const end = this.pathPoints[index + 1];
                const dx = end.x - start.x;
                const dy = end.y - start.y;
                const lengthSquared = dx * dx + dy * dy;
                if (!lengthSquared) continue;
                const t = Math.max(
                    0,
                    Math.min(
                        1,
                        ((pointer.x - start.x) * dx +
                            (pointer.y - start.y) * dy) /
                            lengthSquared
                    )
                );
                const point = { x: start.x + t * dx, y: start.y + t * dy };
                const distance = Math.hypot(
                    pointer.x - point.x,
                    pointer.y - point.y
                );
                if (!nearest || distance < nearest.distance) {
                    nearest = { index, point, distance, t };
                }
            }
            if (!nearest || nearest.t === 0 || nearest.t === 1) return;
            event.cancelBubble = true;
            this.pathPoints.splice(nearest.index + 1, 0, nearest.point);
            this.updateGeometry();
            this.rebuildAnchors(true);
            this.emitEdit();
        });
    }

    getBoundingBox() {
        const bounds = super.getBoundingBox();
        const origin = this.data.pointOrigin;
        if (!origin) return bounds;
        return {
            ...bounds,
            absX: origin.x,
            absY: origin.y,
            minX: bounds.absX - origin.x,
            minY: bounds.absY - origin.y,
            maxX: bounds.absX + bounds.width - origin.x,
            maxY: bounds.absY + bounds.height - origin.y,
        };
    }

    canEditPoints() {
        return (
            this.element.draggable() && !this.isLocked && this.dragger.visible()
        );
    }

    showAnchors() {
        this.anchors.forEach((anchor) => {
            anchor.visible(this.canEditPoints());
            anchor.strokeWidth(2);
        });
    }

    rebuildAnchors(visible = false) {
        this.anchors.forEach((anchor) => anchor.destroy());
        this.anchors = this.pathPoints.map((point, index) => {
            const anchor = new Konva.Circle({
                ...point,
                radius: 6,
                fill: "white",
                stroke: "#0e8ce9",
                strokeWidth: 2,
                name: "anchor",
                draggable: true,
                visible: visible && this.canEditPoints(),
                dragDistance: 3,
            });
            anchor.on("mouseover", () => {
                document.body.style.cursor = "pointer";
            });
            anchor.on("mouseout", () => {
                document.body.style.cursor = "default";
            });
            anchor.on("mousedown touchstart", (event) => {
                if (!this.canEditPoints()) {
                    anchor.stopDrag();
                    return;
                }
                event.cancelBubble = true;
            });
            anchor.on("dragstart dragmove dragend", (event) => {
                event.cancelBubble = true;
                if (!this.canEditPoints()) {
                    anchor.stopDrag();
                    return;
                }
                if (event.type === "dragstart") return;
                this.pathPoints[index] = { x: anchor.x(), y: anchor.y() };
                this.updateGeometry();
                if (event.type === "dragend") this.emitEdit();
            });
            anchor.on("dblclick dbltap", (event) => {
                event.cancelBubble = true;
                // Keep the endpoints so a line always has at least two vertices.
                if (
                    !this.canEditPoints() ||
                    index === 0 ||
                    index === this.pathPoints.length - 1
                )
                    return;
                this.pathPoints.splice(index, 1);
                this.updateGeometry();
                this.rebuildAnchors(true);
                this.emitEdit();
            });
            this.element.add(anchor);
            return anchor;
        });
    }

    updateGeometry() {
        const origin = this.data.pointOrigin;
        this.data.d = this.pathPoints.map((point) => ({
            x: point.x + origin.x,
            y: point.y + origin.y,
        }));
        const points = pointsToPaths(this.pathPoints);
        if (typeof this.currentLine.points === "function") {
            this.currentLine.points(points);
        }
        this.dragger.points(points);
        this.hitbox.points(points);
        const { maxX, maxY, width, height } = this.getBoundingBox();
        this.remover.position({ x: maxX + 10, y: maxY + 10 });
        this.element.size({ width, height });
        this.element.getLayer()?.batchDraw();
    }

    editPoints(points) {
        const visible = this.anchors.some((anchor) => anchor.visible());
        this.pathPoints.splice(
            0,
            this.pathPoints.length,
            ...points.map((point) => ({ ...point }))
        );
        this.updateGeometry();
        this.rebuildAnchors(visible);
    }

    setEditHandler(callback) {
        this.editHandler = callback;
    }

    emitEdit() {
        this.editHandler?.(this.pathPoints.map((point) => ({ ...point })));
    }

    setDragClick(callback) {
        super.setDragClick((event) => {
            callback(event);
            this.showAnchors();
        });
    }

    setDragStart(callback) {
        super.setDragStart((event) => {
            callback(event);
            this.showAnchors();
        });
    }
}

export class Multiline extends EditableMultiline {
    buildElement() {
        const points = pointsToPaths(this.toRelativePoints(this.data.d));
        this.currentLine = new Konva.Line({
            stroke: this.color,
            strokeWidth: SIZE_MAP[this.size],
            points: points,
            lineCap: "round",
            lineJoin: "round",
            name: this.name,
        });

        const { absX, absY } = this.getBoundingBox();
        const group = new Konva.Group({
            draggable: true,
            id: this.id,
            x: absX,
            y: absY,
        });
        group.add(this.currentLine);
        group.add(this.remover);
        group.add(this.dragger);
        group.add(this.hitbox);
        group.setAttrs({
            instance: this,
        });

        return group;
    }
}

export class MultilineHammer extends EditableMultiline {
    buildElement() {
        const points = pointsToPaths(this.toRelativePoints(this.data.d));
        this.currentLine = new Konva.Arrow({
            stroke: this.color,
            strokeWidth: SIZE_MAP[this.size],
            points: points,
            lineCap: "round",
            lineJoin: "round",
            name: this.name,
            pointerWidth: 20,
            pointerLength: 0,
        });

        const { absX, absY } = this.getBoundingBox();
        const group = new Konva.Group({
            draggable: true,
            id: this.id,
            x: absX,
            y: absY,
        });
        group.add(this.currentLine);
        group.add(this.remover);
        group.add(this.dragger);
        group.add(this.hitbox);
        group.setAttrs({
            instance: this,
        });

        return group;
    }
}

export class MultilineArrow extends EditableMultiline {
    buildElement() {
        const _this = this;
        const simplifiedPath = this.pathPoints;
        this.currentLine = new Konva.Shape({
            stroke: this.color,
            strokeWidth: SIZE_MAP[this.size],
            lineCap: "square",
            name: this.name,
            sceneFunc: function (ctx, shape) {
                ctx.beginPath();
                ctx.moveTo(simplifiedPath[0].x, simplifiedPath[0].y);
                for (var i = 1; i < simplifiedPath.length; i++) {
                    ctx.lineTo(simplifiedPath[i].x, simplifiedPath[i].y);
                }
                ctx.fillStrokeShape(shape);
                // ctx.stroke();

                if (simplifiedPath.length >= 2) {
                    const firstPoint =
                        simplifiedPath[simplifiedPath.length - 1];
                    let lastSignificantPoint = firstPoint;
                    const deltaX = lastSignificantPoint.x - firstPoint.x;
                    const deltaY = lastSignificantPoint.y - firstPoint.y;

                    const distanceThreshold = 10;
                    if (
                        Math.abs(deltaX) < distanceThreshold &&
                        Math.abs(deltaY) < distanceThreshold
                    ) {
                        lastSignificantPoint =
                            simplifiedPath[simplifiedPath.length - 2];
                    }

                    drawStraightArrow2(
                        ctx,
                        lastSignificantPoint,
                        firstPoint,
                        _this.color,
                        _this.size
                    );
                }
                ctx.fillStrokeShape(shape);
                ctx.closePath();
            },
        });

        const { absX, absY } = this.getBoundingBox();
        const group = new Konva.Group({
            draggable: true,
            id: this.id,
            x: absX,
            y: absY,
        });
        group.add(this.currentLine);
        group.add(this.remover);
        group.add(this.dragger);
        group.add(this.hitbox);
        group.setAttrs({
            instance: this,
        });

        return group;
    }
}

export class MultilineDotted extends EditableMultiline {
    buildElement() {
        const points = pointsToPaths(this.toRelativePoints(this.data.d));
        this.currentLine = new Konva.Line({
            stroke: this.color,
            strokeWidth: SIZE_MAP[this.size],
            points: points,
            lineCap: "round",
            lineJoin: "round",
            name: this.name,
            dash: [10, 10],
        });

        const { absX, absY } = this.getBoundingBox();
        const group = new Konva.Group({
            draggable: true,
            id: this.id,
            x: absX,
            y: absY,
        });
        group.add(this.currentLine);
        group.add(this.remover);
        group.add(this.dragger);
        group.add(this.hitbox);
        group.setAttrs({
            instance: this,
        });

        return group;
    }
}

export class MultilineDashedArrow extends EditableMultiline {
    buildElement() {
        const _this = this;
        const simplifiedPath = this.pathPoints;
        this.currentLine = new Konva.Shape({
            stroke: this.color,
            strokeWidth: SIZE_MAP[this.size],
            lineCap: "square",
            dash: [10, 10],
            name: this.name,
            sceneFunc: function (ctx, shape) {
                ctx.beginPath();
                ctx.moveTo(simplifiedPath[0].x, simplifiedPath[0].y);
                for (var i = 1; i < simplifiedPath.length; i++) {
                    ctx.lineTo(simplifiedPath[i].x, simplifiedPath[i].y);
                }
                ctx.fillStrokeShape(shape);
                // ctx.stroke();

                if (simplifiedPath.length >= 2) {
                    const firstPoint =
                        simplifiedPath[simplifiedPath.length - 1];
                    let lastSignificantPoint = firstPoint;
                    const deltaX = lastSignificantPoint.x - firstPoint.x;
                    const deltaY = lastSignificantPoint.y - firstPoint.y;

                    const distanceThreshold = 10;
                    if (
                        Math.abs(deltaX) < distanceThreshold &&
                        Math.abs(deltaY) < distanceThreshold
                    ) {
                        lastSignificantPoint =
                            simplifiedPath[simplifiedPath.length - 2];
                    }

                    drawStraightArrow2(
                        ctx,
                        lastSignificantPoint,
                        firstPoint,
                        _this.color,
                        _this.size
                    );
                }
                ctx.fillStrokeShape(shape);
                ctx.closePath();
            },
        });

        const { absX, absY, width, height } = this.getBoundingBox();
        const group = new Konva.Group({
            draggable: true,
            id: this.id,
            x: absX,
            y: absY,
            width,
            height,
        });
        group.add(this.currentLine);
        group.add(this.remover);
        group.add(this.dragger);
        group.add(this.hitbox);
        group.setAttrs({
            instance: this,
        });

        return group;
    }
}

class PenSquigglyLine extends EditableMultiline {
    isSquigglySegment() {
        return true;
    }

    drawSquigglySegment(ctx, start, end) {
        const amplitude = 7;
        const frequency = 20;
        const straightLength = 15;
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);

        if (length === 0) return;

        const steps = Math.ceil(length);
        for (let step = 1; step <= steps; step++) {
            const distance = Math.min(step, length);
            const progress = distance / length;
            const baseX = start.x + progress * dx;
            const baseY = start.y + progress * dy;
            const waveOffset =
                distance < length - straightLength
                    ? Math.sin((distance / frequency) * 2 * Math.PI) * amplitude
                    : 0;

            ctx.lineTo(
                baseX - Math.sin(angle) * waveOffset,
                baseY + Math.cos(angle) * waveOffset
            );
        }
    }

    drawEndCap(ctx, shape, point, angle) {
        const arrowSize = 10;
        const arrowLineLength = 10;
        const endX = point.x + Math.cos(angle) * arrowLineLength;
        const endY = point.y + Math.sin(angle) * arrowLineLength;

        ctx.lineTo(endX, endY);
        ctx.fillStrokeShape(shape);
        ctx.beginPath();

        if (this.name.endsWith("Arrow")) {
            ctx.moveTo(endX, endY);
            ctx.lineTo(
                endX - arrowSize * Math.cos(angle - Math.PI / 4),
                endY - arrowSize * Math.sin(angle - Math.PI / 4)
            );
            ctx.moveTo(endX, endY);
            ctx.lineTo(
                endX - arrowSize * Math.cos(angle + Math.PI / 4),
                endY - arrowSize * Math.sin(angle + Math.PI / 4)
            );
        } else {
            ctx.moveTo(
                endX - Math.sin(angle) * arrowSize,
                endY + Math.cos(angle) * arrowSize
            );
            ctx.lineTo(
                endX + Math.sin(angle) * arrowSize,
                endY - Math.cos(angle) * arrowSize
            );
        }
    }

    buildElement() {
        const simplifiedPath = this.pathPoints;
        this.currentLine = new Konva.Shape({
            stroke: this.color,
            strokeWidth: SIZE_MAP[this.size],
            lineCap: "square",
            name: this.name,
            sceneFunc: (ctx, shape) => {
                if (simplifiedPath.length < 2) return;

                ctx.beginPath();
                ctx.moveTo(simplifiedPath[0].x, simplifiedPath[0].y);
                let lastAngle = 0;

                for (let i = 0; i < simplifiedPath.length - 1; i++) {
                    const start = simplifiedPath[i];
                    const end = simplifiedPath[i + 1];
                    const dx = end.x - start.x;
                    const dy = end.y - start.y;

                    if (dx !== 0 || dy !== 0) {
                        lastAngle = Math.atan2(dy, dx);
                    }

                    if (this.isSquigglySegment(i)) {
                        this.drawSquigglySegment(ctx, start, end);
                    } else {
                        ctx.lineTo(end.x, end.y);
                    }
                }

                this.drawEndCap(
                    ctx,
                    shape,
                    simplifiedPath[simplifiedPath.length - 1],
                    lastAngle
                );
                ctx.closePath();
                ctx.fillStrokeShape(shape);
            },
        });

        const { absX, absY, width, height } = this.getBoundingBox();
        const group = new Konva.Group({
            draggable: true,
            id: this.id,
            x: absX,
            y: absY,
            width,
            height,
        });
        group.add(this.currentLine);
        group.add(this.remover);
        group.add(this.dragger);
        group.add(this.hitbox);
        group.setAttrs({
            instance: this,
        });

        return group;
    }
}

export class PenSquigglyArrow extends PenSquigglyLine {}

export class PenSquigglyFirstArrow extends PenSquigglyLine {
    isSquigglySegment(index) {
        return index === 0;
    }
}

export class BezierLine extends CNLine {
    constructor(data) {
        super(data);

        this.isTab =
            data.t === "bezierLineTab" || data.t === "bezierLineTabDashed";
        this.isDashed =
            data.t === "bezierLineDashed" || data.t === "bezierLineTabDashed";
        this.start = this.buildAnchor({
            x: this.data.d[0],
            y: this.data.d[1],
        });
        this.end = this.buildAnchor({
            x: this.data.d[2],
            y: this.data.d[3],
        });
        this.control1 = this.buildAnchor({
            x: this.data.d[4],
            y: this.data.d[5],
        });
        this.control2 = this.buildAnchor({
            x: this.data.d[6],
            y: this.data.d[7],
        });
        this.id = this.data.d[8];

        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    pasteData(inc) {
        return [
            this.data.d[0] + 30 * inc,
            this.data.d[1] + 30 * inc,
            this.data.d[2] + 30 * inc,
            this.data.d[3] + 30 * inc,
            this.data.d[4] + 30 * inc,
            this.data.d[5] + 30 * inc,
            this.data.d[6] + 30 * inc,
            this.data.d[7] + 30 * inc,
            "tx" + +new Date(),
        ];
    }

    buildAnchor(pos) {
        const { absX, absY } = this.getBoundingBox();
        const { x, y } = pos;
        const anchor = new Konva.Circle({
            x: x - absX,
            y: y - absY,
            radius: 10,
            stroke: "#0e8ce9",
            name: "anchor",
            strokeWidth: 1,
            draggable: true,
            visible: false,
        });

        // Add hover styling
        anchor.on("mouseover", function () {
            document.body.style.cursor = "pointer";
            this.strokeWidth(4);
        });

        anchor.on("mouseout", function () {
            document.body.style.cursor = "default";
            this.strokeWidth(2);
        });

        const _this = this;
        // Update curves when anchor is moved
        anchor.on("dragmove", function () {
            _this.updateDottedLines();
        });

        return anchor;
    }

    buildElement() {
        const _this = this;
        this.currentLine = new Konva.Shape({
            stroke: this.color,
            lineCap: "square",
            strokeWidth: SIZE_MAP[this.size],
            dash: this.isDashed ? [10, 10] : null,
            sceneFunc: (ctx, shape) => {
                ctx.beginPath();
                ctx.moveTo(this.start.x(), this.start.y());
                ctx.bezierCurveTo(
                    this.control1.x(),
                    this.control1.y(),
                    this.control2.x(),
                    this.control2.y(),
                    this.end.x(),
                    this.end.y()
                );

                ctx.fillStrokeShape(shape);

                let startArrow = {
                    x: this.control2.x(),
                    y: this.control2.y(),
                };

                if (
                    this.end.x() === startArrow.x &&
                    this.end.y() === startArrow.y
                ) {
                    startArrow = {
                        x: this.control1.x(),
                        y: this.control1.y(),
                    };
                }

                if (
                    this.end.x() === startArrow.x &&
                    this.end.y() === startArrow.y
                ) {
                    startArrow = {
                        x: this.start.x(),
                        y: this.start.y(),
                    };
                }

                if (this.isTab) {
                    drawBezierHammer(
                        ctx,
                        [
                            startArrow.x,
                            startArrow.y,
                            this.end.x(),
                            this.end.y(),
                        ],
                        _this.color,
                        _this.size
                    );
                } else {
                    drawStraightArrow2(
                        ctx,
                        {
                            x: startArrow.x,
                            y: startArrow.y,
                        },
                        {
                            x: this.end.x(),
                            y: this.end.y(),
                        },
                        _this.color,
                        _this.size
                    );
                }
                ctx.fillStrokeShape(shape);
            },
        });
        const { absX, absY } = this.getBoundingBox();
        const group = new Konva.Group({
            id: this.id,
            draggable: true,
            x: absX,
            y: absY,
        });

        this.linePath = new Konva.Line({
            dash: [10, 10, 0, 10],
            strokeWidth: 3,
            stroke: "#0e8ce9",
            lineCap: "round",
            id: this.id + "-linepath",
            opacity: 0.3,
            points: [0, 0],
            name: "linepath",
            visible: false,
        });

        group.add(this.linePath);
        group.add(this.currentLine);
        group.add(this.hitbox);
        group.add(this.dragger);
        group.add(this.remover);
        group.add(this.start);
        group.add(this.end);
        group.add(this.control1);
        group.add(this.control2);

        group.setAttrs({
            instance: this,
        });

        this.updateDottedLines();

        return group;
    }
    updateDottedLines() {
        // Update control point lines for quadratic curve
        this.linePath.points([
            this.start.x(),
            this.start.y(),
            this.control1.x(),
            this.control1.y(),
            this.control2.x(),
            this.control2.y(),
            this.end.x(),
            this.end.y(),
        ]);
    }

    buildRemover() {
        const { maxX, maxY } = this.getBoundingBox();
        const remover = new Konva.Image({
            image: this.removerImage,
            width: 20,
            height: 20,
            name: "remover",
            x: maxX - 30,
            y: maxY - 20,
            visible: false,
        });
        remover.on("mouseover", () => {
            document.body.style.cursor = "pointer";
        });
        remover.on("mouseout", () => {
            document.body.style.cursor = "default";
        });

        return remover;
    }

    getBoundingBox() {
        const ys = [
            this.data.d[1],
            this.data.d[3],
            this.data.d[5],
            this.data.d[7],
        ];
        const xs = [
            this.data.d[0],
            this.data.d[2],
            this.data.d[4],
            this.data.d[6],
        ];

        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        return {
            absX: minX,
            absY: minY,
            minX: 0,
            maxX: maxX - minX,
            minY: 0,
            maxY: maxY - minY,
            width: maxX - minX,
            height: maxY - minY,
        };
    }

    editBezier(data) {
        const { absX, absY } = this.getBoundingBox();
        this.start.x(data.d[0] - absX);
        this.start.y(data.d[1] - absY);
        this.end.x(data.d[2] - absX);
        this.end.y(data.d[3] - absY);
        this.control1.x(data.d[4] - absX);
        this.control1.y(data.d[5] - absY);
        this.control2.x(data.d[6] - absX);
        this.control2.y(data.d[7] - absY);
        this.data.d = data.d;
        this.updateDottedLines();
        this.updateDragger(data.d);
        this.updateRemover(data.d);
    }

    updateDragger(data) {
        this.data.d = data;
        if (this.name.startsWith("bezierLine")) {
            this.dragger.getLayer()?.batchDraw();
            this.hitbox.getLayer()?.batchDraw();
            return;
        }
        const points = pointsToPaths(this.toRelativePoints());
        this.dragger.points(points);
        this.hitbox.points(points);
    }

    updateRemover(data) {
        this.remover.x(data[2] - 30);
        this.remover.y(data[3] - 20);
    }

    setDragClick(callback) {
        const _this = this;
        function click(e) {
            callback(e, _this.color);
            _this.dragger.strokeWidth(DRAGGER_STROKE_WIDTH);
            _this.start.visible(true);
            _this.end.visible(true);
            _this.control1.visible(true);
            _this.control2.visible(true);
            _this.linePath.visible(true);
            _this.start.strokeWidth(1);
            _this.end.strokeWidth(1);
            _this.control1.strokeWidth(1);
            _this.control2.strokeWidth(1);
            _this.linePath.strokeWidth(3);
        }
        this.element.on("click", (e) => click(e));
        this.element.on("tap", (e) => click(e));
    }

    setDragStart(callback) {
        const _this = this;
        this.element.on("dragstart", (e) => {
            callback(e);
            _this.dragger.strokeWidth(DRAGGER_STROKE_WIDTH);
            _this.start.visible(true);
            _this.end.visible(true);
            _this.control1.visible(true);
            _this.control2.visible(true);
            _this.linePath.visible(true);
            _this.start.strokeWidth(1);
            _this.end.strokeWidth(1);
            _this.control1.strokeWidth(1);
            _this.control2.strokeWidth(1);
            _this.linePath.strokeWidth(3);
        });
    }

    setRemoverAction(callback) {
        this.removeAction = callback;
        this.remover.on("click", () => {
            callback();
        });
        this.remover.on("tap", () => {
            callback();
        });
    }

    props() {
        const { absX, absY } = this.getBoundingBox();
        return [
            this.start.x() + absX,
            this.start.y() + absY,
            this.end.x() + absX,
            this.end.y() + absY,
            this.control1.x() + absX,
            this.control1.y() + absY,
            this.control2.x() + absX,
            this.control2.y() + absY,
        ];
    }

    setEditHandler(callback) {
        const _this = this;

        this.start.on("dragend", () => {
            callback(_this.props());
            _this.updateDragger(_this.props());
            _this.updateRemover(_this.props());
        });
        this.end.on("dragend", () => {
            callback(_this.props());
            _this.updateDragger(_this.props());
        });
        this.control1.on("dragend", () => {
            callback(_this.props());
            _this.updateDragger(_this.props());
        });
        this.control2.on("dragend", () => {
            callback(_this.props());
            _this.updateDragger(_this.props());
        });
    }
}
